import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface OfflineDBSchema extends DBSchema {
  leads: {
    key: string;
    value: any;
    indexes: { 'by-updated': string };
  };
  notes: {
    key: string;
    value: any;
    indexes: { 'by-lead': string };
  };
  tasks: {
    key: string;
    value: any;
    indexes: { 'by-lead': string };
  };
  messages: {
    key: string;
    value: any;
    indexes: { 'by-lead': string };
  };
  appointments: {
    key: string;
    value: any;
    indexes: { 'by-date': string };
  };
  transactions: {
    key: string;
    value: any;
  };
  documents: {
    key: string;
    value: any;
    indexes: { 'by-lead': string };
  };
  syncQueue: {
    key: string;
    value: {
      id: string;
      table: string;
      operation: 'insert' | 'update' | 'delete';
      data: any;
      timestamp: number;
      status: 'pending' | 'syncing' | 'failed';
      retryCount: number;
    };
    indexes: { 'by-status': string; 'by-timestamp': number };
  };
}

class OfflineStorage {
  private db: IDBPDatabase<OfflineDBSchema> | null = null;
  private dbName = 'simpletrackr-offline';
  private version = 1;

  async initialize() {
    this.db = await openDB<OfflineDBSchema>(this.dbName, this.version, {
      upgrade(db) {
        // Leads store
        if (!db.objectStoreNames.contains('leads')) {
          const leadStore = db.createObjectStore('leads', { keyPath: 'id' });
          leadStore.createIndex('by-updated', 'updated_at');
        }

        // Notes store
        if (!db.objectStoreNames.contains('notes')) {
          const noteStore = db.createObjectStore('notes', { keyPath: 'id' });
          noteStore.createIndex('by-lead', 'lead_id');
        }

        // Tasks store
        if (!db.objectStoreNames.contains('tasks')) {
          const taskStore = db.createObjectStore('tasks', { keyPath: 'id' });
          taskStore.createIndex('by-lead', 'lead_id');
        }

        // Messages store
        if (!db.objectStoreNames.contains('messages')) {
          const msgStore = db.createObjectStore('messages', { keyPath: 'id' });
          msgStore.createIndex('by-lead', 'lead_id');
        }

        // Appointments store
        if (!db.objectStoreNames.contains('appointments')) {
          const apptStore = db.createObjectStore('appointments', { keyPath: 'id' });
          apptStore.createIndex('by-date', 'scheduled_at');
        }

        // Transactions store
        if (!db.objectStoreNames.contains('transactions')) {
          db.createObjectStore('transactions', { keyPath: 'id' });
        }

        // Documents store
        if (!db.objectStoreNames.contains('documents')) {
          const docStore = db.createObjectStore('documents', { keyPath: 'id' });
          docStore.createIndex('by-lead', 'lead_id');
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          syncStore.createIndex('by-status', 'status');
          syncStore.createIndex('by-timestamp', 'timestamp');
        }
      },
    });
  }

  async get<T extends keyof OfflineDBSchema>(
    table: T,
    key: string
  ): Promise<OfflineDBSchema[T]['value'] | undefined> {
    if (!this.db) await this.initialize();
    return this.db!.get(table as any, key);
  }

  async getAll<T extends keyof OfflineDBSchema>(
    table: T
  ): Promise<OfflineDBSchema[T]['value'][]> {
    if (!this.db) await this.initialize();
    return this.db!.getAll(table as any);
  }

  async set<T extends keyof OfflineDBSchema>(
    table: T,
    value: OfflineDBSchema[T]['value']
  ): Promise<void> {
    if (!this.db) await this.initialize();
    await this.db!.put(table as any, value);
  }

  async delete<T extends keyof OfflineDBSchema>(
    table: T,
    key: string
  ): Promise<void> {
    if (!this.db) await this.initialize();
    await this.db!.delete(table as any, key);
  }

  async clear<T extends keyof OfflineDBSchema>(table: T): Promise<void> {
    if (!this.db) await this.initialize();
    await this.db!.clear(table as any);
  }

  async addToSyncQueue(
    table: string,
    operation: 'insert' | 'update' | 'delete',
    data: any
  ): Promise<string> {
    if (!this.db) await this.initialize();
    const id = `${table}_${operation}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await this.db!.put('syncQueue', {
      id,
      table,
      operation,
      data,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
    });
    return id;
  }

  async getPendingSyncItems() {
    if (!this.db) await this.initialize();
    return this.db!.getAllFromIndex('syncQueue', 'by-status', 'pending');
  }

  async updateSyncItem(id: string, updates: Partial<OfflineDBSchema['syncQueue']['value']>) {
    if (!this.db) await this.initialize();
    const item = await this.db!.get('syncQueue', id);
    if (item) {
      await this.db!.put('syncQueue', { ...item, ...updates });
    }
  }

  async deleteSyncItem(id: string) {
    if (!this.db) await this.initialize();
    await this.db!.delete('syncQueue', id);
  }

  async getSyncQueueCount(): Promise<number> {
    if (!this.db) await this.initialize();
    const items = await this.getPendingSyncItems();
    return items.length;
  }

  async clearAllData() {
    if (!this.db) await this.initialize();
    const stores = [
      'leads',
      'notes',
      'tasks',
      'messages',
      'appointments',
      'transactions',
      'documents',
      'syncQueue',
    ];
    for (const store of stores) {
      await this.db!.clear(store as any);
    }
  }
}

export const offlineStorage = new OfflineStorage();
